// src/pages/OrderDetail.jsx
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuthenticatedAPI } from "../hooks/useAuthenticatedAPI";
import { Modal } from "react-bootstrap";

const OrderDetail = () => {
  const { id_panier } = useParams();
  const navigate = useNavigate();
  const { apiGet, apiDelete, apiPut } = useAuthenticatedAPI();

  const [panier, setPanier] = useState(null);
  const [facture, setFacture] = useState(null);
  const [livraison, setLivraison] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [showModal, setShowModal] = useState(false);
  const [modalContent, setModalContent] = useState("");
  const [modalAction, setModalAction] = useState(null);

  const formatPrice = (n) => new Intl.NumberFormat("fr-FR").format(n || 0) + " FCFA";

  const calculerSousTotal = () => {
    if (!panier?.Appartenirs) return 0;
    return panier.Appartenirs.reduce(
      (total, line) => total + (line.Article?.prix || 0) * (line.quantite || 1),
      0
    );
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const p = await apiGet(`/panier/getPanierId/${id_panier}`);
      setPanier(p);

      try {
        const f = await apiGet(`/facture/panier/${id_panier}`);
        setFacture(Array.isArray(f) ? f[0] : f);
      } catch {
        setFacture(null);
      }

      if (p?.id_livraison) {
        try {
          const liv = await apiGet(`/livraison/${p.id_livraison}`);
          setLivraison(liv);
        } catch {
          setLivraison(null);
        }
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [id_panier]);

  const sousTotal = calculerSousTotal();
  const reductionGlobale = facture?.remise || 0;
  const deliveryFee = panier?.id_livraison ? 5000 : 0;
  const total = sousTotal - reductionGlobale + deliveryFee;

  // Vérifier si le panier peut être supprimé
  const canDeletePanier = () => {
    if (!panier) return false;
    
    const lastModified = new Date(panier.updated_at || panier.created_at);
    const threeMonthsAgo = new Date();
    threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
    
    const isOldEnough = lastModified <= threeMonthsAgo;
    
    // Peut supprimer si : (etat = 0 ET > 3 mois) OU (etat = 2 ET > 3 mois)
    return isOldEnough && (panier.etat === 0 || panier.etat === 2);
  };

  // Vérifier si le stock peut être traité
  const canTraiterStock = () => {
    if (!panier) return false;
    // Le stock peut être traité si le panier n'a pas déjà été traité (livre !== 2)
    return panier.livre !== 2;
  };


  const confirmDeleteFacture = async () => {
    try {
      await apiDelete(`/facture/deletefacture/${facture.id_fact}`);
      alert("Facture supprimée avec succès.");
      setFacture(null);
      setShowModal(false);
    } catch (err) {
      alert(`Erreur lors de la suppression : ${err.message}`);
      setShowModal(false);
    }
  };

  const handleDeletePanier = async () => {
    if (!canDeletePanier()) {
      const reason = panier.etat === 1 
        ? "Ce panier est en cours et ne peut pas être supprimé."
        : "Ce panier ne peut être supprimé que s'il a plus de 3 mois et que son état est 'En attente' (0) ou 'Terminé' (2).";
      alert(reason);
      return;
    }

    const ageInfo = `Ce panier a plus de 3 mois et son état est "${["En attente", "En cours", "Terminé"][panier.etat]}". ` +
                    `Il peut être supprimé définitivement. Confirmez-vous la suppression ?`;
    
    setModalContent(ageInfo);
    setModalAction(() => confirmDeletePanier);
    setShowModal(true);
  };

  const confirmDeletePanier = async () => {
    try {
      await apiDelete(`/panier/delete/${id_panier}`);
      alert("Panier supprimé avec succès.");
      setShowModal(false);
      navigate("/order");
    } catch (err) {
      alert(`Erreur lors de la suppression : ${err.message}`);
      setShowModal(false);
    }
  };

  const handleTraiterStock = async () => {
    if (!canTraiterStock()) {
      alert("Le stock de ce panier a déjà été traité. Cette opération ne peut être effectuée qu'une seule fois.");
      return;
    }

    setModalContent(
      "Cette action va réduire définitivement le stock disponible pour tous les articles de ce panier. " +
      "Cette opération ne peut être effectuée qu'une seule fois par panier. Confirmez-vous ?"
    );
    setModalAction(() => confirmTraiterStock);
    setShowModal(true);
  };

  const confirmTraiterStock = async () => {
    try {
      await apiPut(`/panier/traiter-stock/${id_panier}`, {});
      alert("Stock traité avec succès !");
      setShowModal(false);
      fetchData();
    } catch (err) {
      const errorMsg = err.message || "Erreur inconnue";
      if (errorMsg.includes("non éligible")) {
        alert("Ce panier a déjà été traité ou n'est pas éligible pour le traitement du stock.");
      } else {
        alert(`Erreur lors du traitement : ${errorMsg}`);
      }
      setShowModal(false);
    }
  };

  const handleMarquerLivraison = async () => {
    if (!livraison || panier.livre !== 0) return;
    
    setModalContent("Confirmer que la livraison a été effectuée ?");
    setModalAction(() => confirmMarquerLivraison);
    setShowModal(true);
  };

  const confirmMarquerLivraison = async () => {
    try {
      await apiPut(`/panier/update/${id_panier}`, { livre: 1 });
      alert("Livraison marquée comme effectuée !");
      setShowModal(false);
      fetchData();
    } catch (err) {
      alert(`Erreur : ${err.message}`);
      setShowModal(false);
    }
  };

  const goBack = () => navigate("/order");

  if (loading) return <div className="container py-4">Chargement...</div>;
  if (error) return <div className="alert alert-danger">{error}</div>;
  if (!panier) return <div className="container py-4">Panier introuvable.</div>;

  const canDelete = canDeletePanier();
  const stockAlreadyProcessed = !canTraiterStock();

  return (
    <div className="container py-4">
      <button className="btn btn-outline-secondary mb-3" onClick={goBack}>
        ⬅ Retour à la liste des commandes
      </button>

      <h3>🧾 Détail commande : Panier #{panier.id_panier}</h3>

      {/* Alertes informatives */}
      {stockAlreadyProcessed && (
        <div className="alert alert-info">
          ℹ️ Le stock de ce panier a déjà été traité.
        </div>
      )}

      {/* Section client */}
      {panier.Client && (
        <div className="card shadow-sm mb-3">
          <div className="card-header bg-info text-white">Client</div>
          <div className="card-body">
            <p><strong>{panier.Client.nom} {panier.Client.prenom}</strong></p>
            <p>Email : {panier.Client.email}</p>
            <p>Téléphone : {panier.Client.tel}</p>
            <p>Adresse : {panier.Client.adresse}</p>
          </div>
        </div>
      )}

      <div className="row">
        {/* Articles + Facture + Actions */}
        <div className="col-md-8">
          <div className="card mb-3 shadow-sm">
            <div className="card-header bg-primary text-white">Articles du panier</div>
            <div className="card-body">
              {(!panier.Appartenirs || panier.Appartenirs.length === 0) ? (
                <div className="text-muted">Aucun article.</div>
              ) : (
                <ul className="list-group mb-3">
                  {panier.Appartenirs.map((line) => (
                    <li key={line.id_app} className="list-group-item d-flex justify-content-between">
                      <div>
                        <strong>{line.Article?.nom}</strong><br/>
                        <small>Prix unitaire : {formatPrice(line.Article?.prix || 0)}</small>
                      </div>
                      <span>{line.quantite} × {formatPrice(line.Article?.prix || 0)}</span>
                    </li>
                  ))}
                </ul>
              )}

              <div className="mt-3 border-top pt-2">
                <p>Sous-total : <strong>{formatPrice(sousTotal)}</strong></p>
                <p>Remise : <strong>-{formatPrice(reductionGlobale)}</strong></p>
                <p>Livraison : <strong>{formatPrice(deliveryFee)}</strong></p>
                <hr/>
                <h5>Total : {formatPrice(total)}</h5>
              </div>

              <div className="mt-3">
                {panier.etat === 0 && (
                  <p className="text-warning">
                    Ce panier ne dispose pas de facture car il est en attente de validation par le client.
                  </p>
                )}
                {facture && (
                  <>
                    <p>📄 Facture : <strong>#{facture.id_fact}</strong></p>
                    <p>Statut : <strong>{facture.statut}</strong></p>
                  </>
                )}
              </div>

              <div className="mt-3 d-flex gap-2 flex-wrap">
                <button
                  className="btn btn-success"
                  onClick={handleMarquerLivraison}
                  disabled={!livraison || panier.livre !== 0}
                  title={
                    !livraison 
                      ? "Pas de livraison associée" 
                      : panier.livre !== 0 
                      ? "Livraison déjà confirmée" 
                      : "Marquer comme livré"
                  }
                >
                  {panier.livre === 0 ? "Marquer livraison effectuée" : "Livraison confirmée"}
                </button>

                <button
                  className="btn btn-primary"
                  onClick={handleTraiterStock}
                  disabled={stockAlreadyProcessed}
                  title={
                    stockAlreadyProcessed 
                      ? "Stock déjà traité pour ce panier" 
                      : "Traiter le stock (réduction des quantités)"
                  }
                >
                  {stockAlreadyProcessed ? "Stock déjà traité" : "Traiter stock"}
                </button>

                

                <button
                  className="btn btn-outline-danger"
                  onClick={handleDeletePanier}
                  disabled={!canDelete}
                  title={
                    canDelete
                      ? "Supprimer ce panier (plus de 3 mois)"
                      : "Ce panier ne peut pas être supprimé"
                  }
                >
                  Supprimer panier
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Livraison + Statut */}
        <div className="col-md-4">
          <div className="card shadow-sm mb-3">
            <div className="card-header bg-info text-white">Livraison</div>
            <div className="card-body">
              {livraison ? (
                <>
                  <p><strong>{livraison.nom_des}</strong></p>
                  <p>{livraison.ville}</p>
                  <p>{livraison.adresse}</p>
                  <p><small>{new Date(livraison.date_liv).toLocaleDateString()}</small></p>
                </>
              ) : (
                <p className="text-muted">Pas encore de livraison.</p>
              )}
            </div>
          </div>

          <div className="card shadow-sm">
            <div className="card-header bg-secondary text-white">Statut</div>
            <div className="card-body">
              <p>État : <strong>{["En attente", "En cours", "Terminé"][panier.etat] || "?"}</strong></p>
              <p>Livré : <strong>{["Non livré", "En cours", "Livré"][panier.livre] || "?"}</strong></p>
              <p>Valeur : <strong>{formatPrice(panier.valeur)}</strong></p>
              {panier.updated_at && (
                <p>
                  <small>Dernière modification : {new Date(panier.updated_at).toLocaleDateString()}</small>
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modal explicatif */}
      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirmation</Modal.Title>
        </Modal.Header>
        <Modal.Body>{modalContent}</Modal.Body>
        <Modal.Footer>
          <button className="btn btn-secondary" onClick={() => setShowModal(false)}>
            Annuler
          </button>
          <button 
            className="btn btn-primary" 
            onClick={() => modalAction && modalAction()}
          >
            Confirmer
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default OrderDetail;